import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';

const VoiceGuide = ({ exercise, isActive, onComplete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const speechRef = useRef(null);
  const timeoutRef = useRef(null);

  // Avatar image - representing an African American female guide
  const avatarImage = "https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=150&h=150&fit=crop&crop=face";

  // Welcome messages and instructions for each exercise type
  const exerciseGuides = {
    box_breathing: {
      welcome: "Hello beautiful! I'm here to guide you through your Box Breathing with Hand Tracing exercise. This is a wonderful way to find your calm and center yourself. Are you ready to begin this healing journey together?",
      instructions: [
        "First, find a comfortable position and take a deep breath with me. You're doing great.",
        "Now, hold up one hand in front of you. This hand will be your guide to peace.",
        "Take your other pointer finger - this will be your tracing tool for calm.",
        "As you slowly trace up your thumb, breathe in deeply through your nose. Feel that breath filling you with peace.",
        "Now trace down your thumb as you exhale slowly. Let go of any tension you're holding.",
        "Continue with each finger - up on the inhale, down on the exhale. You're creating your own rhythm of peace.",
        "Complete all five fingers. Feel how your body is becoming more relaxed with each breath.",
        "You're almost done! Repeat this cycle 2 more times if it feels good to you.",
        "Beautiful work! You've just given yourself the gift of calm. Take a moment to notice how you feel right now."
      ],
      closing: "You did wonderfully! Remember, this technique is always here for you whenever you need to find your center. You are stronger than you know."
    },
    silent_lion: {
      welcome: "Hey there, powerful soul! I'm here to guide you through the Silent Lion Breath. This exercise will help you release stress and connect with your inner strength. Let's unlock that powerful energy within you!",
      instructions: [
        "Find your comfortable space - seated or standing, whatever feels right for your body today.",
        "Take a deep breath in through your nose. Feel that breath filling you with power and strength.",
        "Now, open your mouth wide like you're about to let out the most powerful roar - but we'll keep it silent.",
        "Stretch your hands and arms wide! Feel yourself taking up space - you deserve to be here.",
        "Exhale with that silent roar - let all that stress and tension flow right out of you.",
        "Let your shoulders drop and feel that release. Your body knows how to let go.",
        "That was beautiful! Let's do that two more times together.",
        "Feel that energy, that power flowing through you. You are mighty and capable."
      ],
      closing: "Incredible! You just connected with your inner lion. That strength and power lives within you always. Carry this feeling with you today."
    },
    body_tap: {
      welcome: "Welcome, dear one! I'm here to guide you through the 'I Am Here' Body Tap. This beautiful practice will help you ground yourself and remember your worth. Let's create some loving connection with your body.",
      instructions: [
        "Start by taking a moment to honor your body - it carries you through every day.",
        "Begin with gentle tapping on your shoulders. As you tap, whisper or think 'I am here, I am okay.'",
        "Move to tapping your arms from shoulder to wrist. Feel yourself acknowledging every part of you.",
        "Gently tap your chest area. This is where your beautiful heart beats - thank it for all it does.",
        "Continue down to tap your legs. These legs support you and carry you forward.",
        "With each tap, remind yourself: 'I am here. I am okay. I am worthy of love and care.'",
        "Now, place one hand over your heart. Feel that rhythm of life within you.",
        "Take one deep, loving breath. You are exactly where you need to be right now."
      ],
      closing: "You are so loved and valued. This gentle practice reminded your body and spirit that you are here, you are okay, and you matter. Hold onto that truth."
    },
    chair_twist: {
      welcome: "Hello lovely! I'm here to help you with your Chair Twist Release. This gentle exercise will help you find peace right where you are. No need to go anywhere - healing can happen right in this moment.",
      instructions: [
        "Settle into your chair and get comfortable. Your body deserves comfort and care.",
        "Hold up one hand in front of you - this will be your anchor to the present moment.",
        "Take your other pointer finger - this will guide you to calm.",
        "Slowly trace up and down each finger on your raised hand. There's no rush here.",
        "As you trace up each finger, breathe in slowly and deeply.",
        "As you trace down, let that breath out and release any tension you're holding.",
        "Complete all five fingers. Feel how this simple movement creates such peace.",
        "Notice how your breathing has become more steady and calm."
      ],
      closing: "Beautiful work! You just proved that healing and peace can happen anywhere, anytime. You have everything you need within you."
    },
    tap_focus: {
      welcome: "Hey there, focused one! I'm here to guide you through the Tap and Focus Reset. In just 30 seconds, we're going to help your mind find its center again. Ready to reset together?",
      instructions: [
        "Place your fingers gently on the desk or surface in front of you.",
        "Feel your feet on the floor - you are grounded and supported.",
        "Start tapping slowly and gently. Feel each tap connecting you to this moment.",
        "Now try switching to a different tempo - faster or slower, whatever feels right.",
        "Focus only on the feeling under your fingertips and the rhythm you're creating.",
        "Let this rhythm clear your mind and bring you into the now.",
        "Just a few more seconds - you're doing beautifully.",
        "Perfect! Feel how much clearer and more focused you are right now."
      ],
      closing: "Amazing! In just 30 seconds, you reset your entire focus. Your mind is powerful, and you just proved you can guide it back to clarity anytime you need."
    },
    five_senses: {
      welcome: "Greetings, mindful soul! I'm here to take you on a beautiful journey through your Five Senses Grounding Check-In. We're going to connect you with the present moment using the amazing senses you were born with. Let's explore together!",
      instructions: [
        "Take a moment to get comfortable. You can do this silently or speak aloud - whatever feels right for you.",
        "Let's start with your beautiful eyes. Look around and name 5 things you can see. Really notice them.",
        "Now, let's use your sense of touch. Identify 4 things you can feel right now - your clothes, the air, a surface.",
        "Listen with your wonderful ears. What are 3 things you can hear in this moment? Near or far, loud or soft.",
        "Take a gentle breath in. What are 2 things you can smell right now? Your senses are so powerful.",
        "Finally, what's 1 thing you can taste, or remember tasting? Let that memory or sensation ground you.",
        "Take a moment to appreciate this present moment and all your amazing senses.",
        "Feel how connected you are to right here, right now."
      ],
      closing: "You are so present and aware! Your senses just brought you fully into this moment. This is your superpower - you can use it anytime to find your center."
    }
  };

  const currentGuide = exerciseGuides[exercise?.type] || exerciseGuides.box_breathing;

  // Text-to-speech function
  const speak = (text, callback) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      
      // Try to use a female voice, preferably with African American characteristics
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.name.includes('Zira') || 
        voice.name.includes('Female') || 
        (voice.gender === 'female' && voice.lang.includes('en'))
      ) || voices.find(voice => voice.lang.includes('en'));
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      utterance.rate = 0.85; // Slightly slower for wellness guidance
      utterance.pitch = 1.1; // Slightly higher pitch
      utterance.volume = 0.8;
      
      utterance.onstart = () => setIsPlaying(true);
      utterance.onend = () => {
        setIsPlaying(false);
        if (callback) callback();
      };
      utterance.onerror = () => {
        setIsPlaying(false);
        if (callback) callback();
      };
      
      speechRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    } else {
      // Fallback if speech synthesis not available
      if (callback) {
        timeoutRef.current = setTimeout(callback, 3000);
      }
    }
  };

  const startGuidedExercise = () => {
    setHasStarted(true);
    setCurrentStep(0);
    speak(currentGuide.welcome, () => {
      // After welcome, start first instruction
      setTimeout(() => speakInstruction(0), 1000);
    });
  };

  const speakInstruction = (stepIndex) => {
    if (stepIndex < currentGuide.instructions.length) {
      setCurrentStep(stepIndex);
      speak(currentGuide.instructions[stepIndex], () => {
        // Move to next instruction after a pause
        setTimeout(() => {
          const nextStep = stepIndex + 1;
          if (nextStep < currentGuide.instructions.length) {
            speakInstruction(nextStep);
          } else {
            // Exercise complete, speak closing
            setTimeout(() => {
              speak(currentGuide.closing, () => {
                setTimeout(() => {
                  if (onComplete) onComplete();
                }, 2000);
              });
            }, 2000);
          }
        }, 2000); // 2 seconds between instructions
      });
    }
  };

  const stopSpeech = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsPlaying(false);
  };

  const skipToNext = () => {
    stopSpeech();
    const nextStep = currentStep + 1;
    if (nextStep < currentGuide.instructions.length) {
      speakInstruction(nextStep);
    }
  };

  useEffect(() => {
    return () => {
      stopSpeech();
    };
  }, []);

  if (!isActive) return null;

  return (
    <Card className="glass-card p-6 mb-6" data-testid="voice-guide">
      <div className="flex items-center space-x-4 mb-6">
        {/* Avatar */}
        <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-teal-200 shadow-lg">
          <img 
            src={avatarImage}
            alt="Wellness Guide"
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to a simple avatar if image fails to load
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 24 24' fill='%2314b8a6'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
            }}
          />
        </div>
        
        {/* Guide Info */}
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Your Wellness Guide 🌟
          </h3>
          <p className="text-blue-600 font-medium">
            {hasStarted ? (
              isPlaying ? (
                <span className="flex items-center">
                  <span className="animate-pulse mr-2">🎤</span>
                  Speaking... (Step {currentStep + 1} of {currentGuide.instructions.length})
                </span>
              ) : (
                "Ready for your next instruction"
              )
            ) : (
              "Ready to guide you through this healing exercise"
            )}
          </p>
        </div>
        
        {/* Status Indicator */}
        <div className="text-right">
          {isPlaying && (
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-red-600 font-medium">Speaking</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Controls */}
      <div className="flex flex-wrap gap-3">
        {!hasStarted ? (
          <Button 
            onClick={startGuidedExercise}
            className="btn-primary"
            data-testid="start-voice-guide-btn"
          >
            🎤 Start Guided Exercise
          </Button>
        ) : (
          <>
            <Button 
              onClick={stopSpeech}
              variant="outline"
              className="btn-secondary"
              disabled={!isPlaying}
              data-testid="pause-voice-btn"
            >
              ⏸️ Pause
            </Button>
            
            <Button 
              onClick={skipToNext}
              variant="outline"
              className="btn-secondary"
              disabled={currentStep >= currentGuide.instructions.length - 1}
              data-testid="skip-instruction-btn"
            >
              ⏭️ Next Step
            </Button>
            
            <Button 
              onClick={() => {
                stopSpeech();
                if (onComplete) onComplete();
              }}
              variant="outline"
              className="btn-secondary"
              data-testid="skip-guide-btn"
            >
              ⏩ Skip Guide
            </Button>
          </>
        )}
      </div>
      
      {/* Progress Indicator */}
      {hasStarted && (
        <div className="mt-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{currentStep + 1} of {currentGuide.instructions.length + 1}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-sky-400 to-cyan-400 h-2 rounded-full transition-all duration-500"
              style={{
                width: `${((currentStep + 1) / (currentGuide.instructions.length + 1)) * 100}%`
              }}
            ></div>
          </div>
        </div>
      )}
      
      {/* Current Instruction Display */}
      {hasStarted && currentStep < currentGuide.instructions.length && (
        <div className="mt-4 p-4 bg-teal-50 rounded-lg">
          <p className="text-teal-800 font-medium text-center italic">
            "{currentGuide.instructions[currentStep]}"
          </p>
        </div>
      )}
    </Card>
  );
};

export default VoiceGuide;
